import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import styles from "./style.module.scss";
import {
  DeleteImageService,
  GetAllImagesService,
} from "shared/services/images";

interface ImagesData {
  id: number;
  title: string;
  desc: string;
  btnText: string;
  link: string;
  link2: string;
  image: Image;
}
interface Image {
  fullpath: string;
}

interface ImageListingsProps {
  handleUpdate: void | any;
}

const ImageListings = ({ handleUpdate }: ImageListingsProps) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: string | null;
    image: ImagesData | null;
  }>({
    open: false,
    action: null,
    image: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
  const [images, setImages] = useState<ImagesData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const openConfirmModal = (action: string, image: ImagesData) => {
    if (action === "Update" && handleUpdate) {
      handleUpdate(image);
    } else {
      setConfirmModal({ open: true, action, image });
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, action: null, image: null });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handleGetAllimages();
  };

  const imagesTableHeads = [
    { title: "Image", key: "image" },
    { title: "Title", key: "title" },
    { title: "Description", key: "desc" },
    { title: "Button Text", key: "btnText" },
    { title: "Button Link", key: "link" },
    { title: "Button Second Link", key: "link2" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllimages = async () => {
    try {
      setLoading(true);
      const response = await GetAllImagesService();
      setImages(response.data.data || []);
      setTotalItems(response.data.data.totalItems || 0);
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetAllimages();
  }, []);

  const handleDeleteImage = async (imageId: number) => {
    setDeleteLoading(imageId);
    try {
      await DeleteImageService(imageId);
      toastMessage("success", "Image deleted successfully!");
      setImages((previmages) =>
        previmages.filter((image) => image.id !== imageId)
      );
    } catch (error) {
      console.error("Error deleting image:", error);
      toastMessage("error", "Failed to delete image. Please try again.");
    } finally {
      setDeleteLoading(null);
      closeConfirmModal();
    }
  };

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={imagesTableHeads}
          title="images List"
          isEmpty={!loading && !images.length}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : images.map((data) => (
                <tr key={data.id}>
                  <td>
                    {data.image?.fullpath ? (
                      <img
                        src={data.image.fullpath}
                        alt={data.title}
                        width="100"
                      />
                    ) : (
                      <p>No Image Available</p>
                    )}
                  </td>
                  <td>{data.title}</td>
                  <td>{data.desc}</td>
                  <td>{data.btnText}</td>
                  <td>{data.link}</td>
                  <td>{data.link2}</td>
                  <td className="">
                    <div className={classNames(styles.IconsContainer)}>
                      <CustomToolTip label="Update">
                        <button
                          onClick={() => openConfirmModal("Update", data)}
                        >
                          <Icons.UpdateBlog />
                        </button>
                      </CustomToolTip>
                      <CustomToolTip label="Delete">
                        <button
                          onClick={() => openConfirmModal("delete", data)}
                          disabled={deleteLoading === data.id}
                        >
                          {deleteLoading === data.id ? (
                            <SkeletonLoader />
                          ) : (
                            <Icons.Cross />
                          )}
                        </button>
                      </CustomToolTip>
                    </div>
                  </td>
                </tr>
              ))}
        </CustomTable>
      </div>

      <CustomPagination
        onPageChange={handlePageChange}
        totalCount={totalItems}
        currentPage={currentPage}
        pageSize={10}
      />
      {confirmModal.open && (
        // @ts-ignore
        <ConfirmationModal
          show={confirmModal.open}
          title="Confirm Delete"
          actionButtonText="Yes, Delete"
          subtitle={`Are you sure you want to delete this image?`}
          loading={deleteLoading === confirmModal.image?.id}
          handleSubmit={() =>
            confirmModal.image && handleDeleteImage(confirmModal.image.id)
          }
          handleClose={closeConfirmModal}
        />
      )}
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 7 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default ImageListings;
